import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';
import { useToast } from '@/components/Toast';
import { SwipeableRow } from '@/components/ui/SwipeableRow';
import { usePlaidLink } from 'react-plaid-link';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { UpgradeModal } from '@/components/UpgradeModal';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Plaid ecosystem detection patterns
const ECOSYSTEM_PATTERNS = {
  apple: /apple\.com|apple\s*pay|itunes|app\s*store|apple\s*services|apple\s*bill/i,
  google: /google\s*\*|google\s*play|google\s*services|google\s*storage|goog\s*\*/i
};

const detectEcosystemBill = (bill) => {
  const nameToCheck = `${bill.name || ''} ${bill.merchant_name || ''}`.toLowerCase();
  if (ECOSYSTEM_PATTERNS.apple.test(nameToCheck)) return { isEcosystem: true, platform: 'apple' };
  if (ECOSYSTEM_PATTERNS.google.test(nameToCheck)) return { isEcosystem: true, platform: 'google' };
  return { isEcosystem: false, platform: null };
};

const CATEGORIES = [
  'HOUSING',
  'LOANS',
  'ENTERTAINMENT',
  'SAVINGS',
  'Insurance',
  'Internet',
  'PHONE',
  'OTHER',
];

const FREQUENCIES = ['Monthly', 'Every 2 Weeks', 'Annual'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function Bills() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { canUsePlaid, canAccessFeature, loading: subLoading } = useSubscription();
  const [bills, setBills] = useState([]);
  const [debtBills, setDebtBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBill, setEditingBill] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [emergencyFundData, setEmergencyFundData] = useState(null);
  const formRef = useRef(null);
  const billRefs = useRef({});
  const [scrollToBillId, setScrollToBillId] = useState(null);
  // Rename modal state for ecosystem charges
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingBill, setRenamingBill] = useState(null);
  const [newBillName, setNewBillName] = useState('');
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Plaid integration state
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);
  const [plaidAvailable, setPlaidAvailable] = useState(false);
  const [plaidNeedsSubscription, setPlaidNeedsSubscription] = useState(false);
  const [showPlaidImport, setShowPlaidImport] = useState(false);
  const [plaidBills, setPlaidBills] = useState([]);
  const [showAliasCatcher, setShowAliasCatcher] = useState(false);
  const [ecosystemBills, setEcosystemBills] = useState([]);
  const [aliases, setAliases] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    amount: 0,
    category: 'HOUSING',
    due_day: 1,
    frequency: 'Monthly',
    annual_month: 1,
    reminders_enabled: false,
    reminder_days: [],
    is_subscription: false,
  });

  // Initialize Plaid Link Token
  useEffect(() => {
    const initPlaid = async () => {
      try {
        const response = await apiPost('/plaid/create-link-token');
        if (response.ok) {
          const data = await response.json();
          setPlaidLinkToken(data.link_token);
          setPlaidAvailable(true);
          setPlaidNeedsSubscription(false);
        } else if (response.status === 403) {
          // Check if subscription is required
          const data = await response.json();
          if (data.detail === 'subscription_required') {
            console.log('Plaid requires subscription');
            setPlaidAvailable(false);
            setPlaidNeedsSubscription(true);
          }
        }
      } catch (error) {
        console.log('Plaid not configured');
        setPlaidAvailable(false);
      }
    };
    initPlaid();
  }, []);

  // Plaid Link success handler
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setPlaidLoading(true);
    try {
      const response = await apiPost('/plaid/exchange-public-token', {
        public_token: publicToken,
        institution: metadata?.institution
      });
      
      if (response.ok) {
        const data = await response.json();
        const fetchedBills = data.recurring_transactions || [];
        
        if (fetchedBills.length === 0) {
          toast.info('No recurring bills found in your account');
          setPlaidLoading(false);
          return;
        }
        
        // Separate ecosystem bills from regular bills
        const regular = [];
        const ecosystem = [];
        
        fetchedBills.forEach(bill => {
          const { isEcosystem, platform } = detectEcosystemBill(bill);
          if (isEcosystem) {
            ecosystem.push({ ...bill, platform, original_plaid_name: bill.name });
          } else {
            regular.push(bill);
          }
        });
        
        setPlaidBills(regular);
        setEcosystemBills(ecosystem);
        
        if (ecosystem.length > 0) {
          // Initialize aliases
          const initialAliases = {};
          ecosystem.forEach((_, idx) => { initialAliases[idx] = ''; });
          setAliases(initialAliases);
          setShowAliasCatcher(true);
        } else {
          setShowPlaidImport(true);
        }
        
        toast.success(`Found ${fetchedBills.length} recurring bills!`);
      } else {
        toast.error('Failed to fetch bills from bank');
      }
    } catch (error) {
      console.error('Plaid exchange error:', error);
      toast.error('Error connecting to bank');
    } finally {
      setPlaidLoading(false);
    }
  }, [toast]);

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err) => {
      if (err) console.log('Plaid exit error:', err);
    }
  });

  useEffect(() => {
    loadBills();
    loadDebtBills();
    loadEmergencyFundData();
  }, []);

  // Scroll to bill after it's been updated
  useEffect(() => {
    if (scrollToBillId && billRefs.current[scrollToBillId]) {
      // Small delay to ensure DOM is updated after bills reload
      setTimeout(() => {
        billRefs.current[scrollToBillId]?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setScrollToBillId(null);
      }, 100);
    }
  }, [bills, scrollToBillId]);

  const loadBills = async () => {
    try {
      const response = await apiGet('/bills');
      if (response.ok) {
        const data = await response.json();
        setBills(data);
      }
    } catch (error) {
      console.error('Error loading bills:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDebtBills = async () => {
    try {
      const response = await apiGet('/debts/as-bills');
      if (response.ok) {
        const data = await response.json();
        setDebtBills(data.debt_bills || []);
      }
    } catch (error) {
      console.error('Error loading debt bills:', error);
    }
  };

  const loadEmergencyFundData = async () => {
    try {
      const response = await apiGet('/emergency-fund');
      if (response.ok) {
        const data = await response.json();
        setEmergencyFundData(data);
      }
    } catch (error) {
      console.error('Error loading emergency fund data:', error);
    }
  };

  const toggleEmergencyFund = async (billId, include) => {
    try {
      await apiPut(`/emergency-fund/bill/${billId}`, { include_in_emergency_fund: include });
      
      // Reload both bills and emergency fund data
      loadBills();
      loadEmergencyFundData();
    } catch (error) {
      console.error('Error toggling emergency fund:', error);
    }
  };

  // Get unique categories from existing bills
  const getExistingCategories = () => {
    const uniqueCategories = [...new Set(bills.map(b => b.category))];
    const allCategories = [...new Set([...CATEGORIES, ...uniqueCategories])];
    return allCategories.sort();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Determine final category
    const finalCategory = formData.category === 'CUSTOM' ? customCategory : formData.category;
    
    if (formData.category === 'CUSTOM' && !customCategory.trim()) {
      alert('Please enter a custom category name');
      return;
    }

    const submitData = {
      ...formData,
      category: finalCategory
    };

    try {
      if (editingBill) {
        // Update existing bill
        const billIdToScrollTo = editingBill.id;  // Save ID before resetting
        const response = await apiPut(`/bills/${editingBill.id}`, submitData);
        if (response.ok) {
          setScrollToBillId(billIdToScrollTo);  // Set scroll target
          loadBills();
          resetForm();
          toast.success('Bill updated successfully!');
        }
      } else {
        // Create new bill
        const response = await apiPost('/bills', submitData);
        if (response.ok) {
          loadBills();
          resetForm();
          toast.success('Bill added successfully!');
        }
      }
    } catch (error) {
      console.error('Error saving bill:', error);
      toast.error('Failed to save bill. Please try again.');
    }
  };

  const handleDelete = async (billId) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) return;

    try {
      const response = await apiDelete(`/bills/${billId}`);
      
      if (response.ok) {
        loadBills();
        toast.success('Bill deleted successfully!');
      } else {
        const error = await response.json();
        console.error('Delete failed:', error);
        toast.error('Failed to delete bill: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting bill:', error);
      toast.error('Error deleting bill. Please try again.');
    }
  };

  const handleEdit = (bill) => {
    setEditingBill(bill);
    const isCustomCategory = !CATEGORIES.includes(bill.category);
    setFormData({
      name: bill.name,
      amount: bill.amount,
      category: isCustomCategory ? 'CUSTOM' : bill.category,
      due_day: bill.due_day,
      frequency: bill.frequency,
      annual_month: bill.annual_month || 1,
      reminders_enabled: bill.reminders_enabled || false,
      reminder_days: bill.reminder_days || [],
      is_subscription: bill.is_subscription || false,
    });
    setCustomCategory(isCustomCategory ? bill.category : '');
    setShowAddForm(true);
    // Scroll to the form after a brief delay to ensure it's rendered
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const resetForm = () => {
    setEditingBill(null);
    setShowAddForm(false);
    setCustomCategory('');
    setFormData({
      name: '',
      amount: 0,
      category: 'HOUSING',
      due_day: 1,
      frequency: 'Monthly',
      annual_month: 1,
      reminders_enabled: false,
      reminder_days: [],
      is_subscription: false,
    });
  };

  const handleReminderToggle = (days) => {
    const currentReminders = formData.reminder_days || [];
    if (currentReminders.includes(days)) {
      // Remove this reminder day
      setFormData({
        ...formData,
        reminder_days: currentReminders.filter(d => d !== days)
      });
    } else {
      // Add this reminder day
      setFormData({
        ...formData,
        reminder_days: [...currentReminders, days].sort((a, b) => b - a)
      });
    }
  };

  // Handler for renaming ecosystem bills
  const handleRenameEcosystemBill = (bill) => {
    setRenamingBill(bill);
    setNewBillName(bill.name);
    setShowRenameModal(true);
  };

  const submitRename = async () => {
    if (!renamingBill || !newBillName.trim()) return;
    
    try {
      const response = await apiPut(`/bills/${renamingBill.id}`, {
        name: newBillName.trim(),
        alias: newBillName.trim()
      });
      
      if (response.ok) {
        toast.success(`Bill renamed to "${newBillName.trim()}"!`);
        setShowRenameModal(false);
        setRenamingBill(null);
        setNewBillName('');
        loadBills();
      } else {
        toast.error('Failed to rename bill');
      }
    } catch (error) {
      console.error('Error renaming bill:', error);
      toast.error('Error renaming bill');
    }
  };

  // Handle alias completion for ecosystem bills
  const handleAliasComplete = () => {
    const aliasedBills = ecosystemBills.map((bill, idx) => ({
      ...bill,
      name: aliases[idx] || bill.name,
      alias: aliases[idx] || null,
      is_ecosystem_charge: true
    }));
    
    setPlaidBills(prev => [...prev, ...aliasedBills]);
    setShowAliasCatcher(false);
    setShowPlaidImport(true);
  };

  // Import selected Plaid bills
  const handleImportPlaidBills = async (selectedBills) => {
    setPlaidLoading(true);
    try {
      let importedCount = 0;
      for (const bill of selectedBills) {
        const response = await apiPost('/bills', {
          name: bill.name,
          amount: bill.amount,
          category: bill.category || '📱 Subscriptions',
          frequency: bill.frequency || 'Monthly',
          source: 'plaid',
          alias: bill.alias || null,
          original_plaid_name: bill.original_plaid_name || null,
          is_ecosystem_charge: bill.is_ecosystem_charge || false,
          platform: bill.platform || null
        });
        if (response.ok) importedCount++;
      }
      
      toast.success(`Imported ${importedCount} bills!`);
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      
      setShowPlaidImport(false);
      setPlaidBills([]);
      loadBills();
    } catch (error) {
      console.error('Error importing bills:', error);
      toast.error('Error importing bills');
    } finally {
      setPlaidLoading(false);
    }
  };

  const groupedBills = bills.reduce((acc, bill) => {
    if (!acc[bill.category]) acc[bill.category] = [];
    acc[bill.category].push(bill);
    return acc;
  }, {});

  // Separate subscriptions from regular bills
  const subscriptionBills = bills.filter(bill => bill.is_subscription);
  const regularBills = bills.filter(bill => !bill.is_subscription);
  
  const groupedRegularBills = regularBills.reduce((acc, bill) => {
    if (!acc[bill.category]) acc[bill.category] = [];
    acc[bill.category].push(bill);
    return acc;
  }, {});

  // Toggle subscription status
  const toggleSubscription = async (billId, currentStatus) => {
    try {
      const response = await apiPut(`/bills/${billId}`, {
        is_subscription: !currentStatus
      });
      if (response.ok) {
        toast.success(!currentStatus ? 'Moved to Subscriptions' : 'Moved to Bills');
        loadBills();
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
      toast.error('Failed to update bill');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading bills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" role="main" aria-label="Bills Management">
      {/* Header - More Compact */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-4 text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold" data-testid="bills-title">
              <span aria-hidden="true">📝</span> Bills Management
            </h1>
            <p className="text-blue-100 text-sm">Manage your recurring bills and expenses</p>
          </div>
          <div className="flex gap-2">
            {plaidAvailable && (
              <button
                onClick={() => plaidReady && openPlaid()}
                disabled={!plaidReady || plaidLoading}
                data-testid="link-bank-button"
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg font-medium transition-colors text-sm flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label={plaidLoading ? "Connecting to Plaid" : "Connect bank with Plaid"}
              >
                {plaidLoading ? (
                  <span className="animate-spin" aria-hidden="true">⏳</span>
                ) : (
                  <span aria-hidden="true">🏦</span>
                )}
                Use Plaid
              </button>
            )}
            <button
              onClick={() => setShowAddForm(true)}
              data-testid="add-bill-button"
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Add new bill"
            >
              + Add Bill
            </button>
          </div>
        </div>
      </header>

      {/* Plaid Sync Card - Show when Plaid is available but user hasn't synced */}
      {plaidAvailable && bills.filter(b => b.source === 'plaid').length === 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-2xl flex-shrink-0">
              🏦
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-emerald-900">Auto-Import Your Bills</h3>
              <p className="text-sm text-emerald-700 mt-1">
                Securely connect your bank to automatically detect recurring subscriptions and bills. 
                We'll find Netflix, Spotify, utilities, and more!
              </p>
              <button
                onClick={() => plaidReady && openPlaid()}
                disabled={!plaidReady || plaidLoading}
                className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium text-sm transition-colors"
                data-testid="plaid-promo-button"
              >
                {plaidLoading ? 'Connecting...' : '🔒 Connect Bank Securely'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Upgrade Prompt - Show when subscription required for Plaid */}
      {plaidNeedsSubscription && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-2xl flex-shrink-0">
              ⭐
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-purple-900">Upgrade to Premium</h3>
              <p className="text-sm text-purple-700 mt-1">
                Connect your bank to automatically import bills with Plaid Bank Sync. 
                Plus get AI insights, full history access, and more!
              </p>
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors"
                data-testid="upgrade-premium-button"
              >
                View Premium Plans →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        feature="bank_sync"
      />

      {/* Info Banner - Debt sync explanation - Collapsible */}
      {debtBills.length > 0 && (
        <details className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl group">
          <summary className="flex items-center gap-3 p-3 cursor-pointer hover:bg-purple-100 rounded-xl transition-colors list-none">
            <span className="text-lg">💳</span>
            <span className="text-sm text-purple-900 font-semibold">
              {debtBills.length} bill{debtBills.length !== 1 ? 's' : ''} synced from Debt Snowball
            </span>
            <svg className="w-4 h-4 ml-auto text-purple-600 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="px-3 pb-3 pt-1 text-sm text-purple-700">
            These are your loan and credit card minimum payments. Edit them in the Debt Snowball calculator to update them here.
          </div>
        </details>
      )}

      {/* Emergency Fund Banner - Collapsible */}
      {emergencyFundData && (
        <details className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl group">
          <summary className="flex items-center justify-between p-3 cursor-pointer hover:bg-emerald-100 rounded-xl transition-colors list-none">
            <div className="flex items-center gap-3">
              <span className="text-lg">🛡️</span>
              <div>
                <span className="text-sm font-semibold text-emerald-900">Emergency Fund Calculator</span>
                {emergencyFundData.settings?.is_active && (
                  <span className="text-xs text-emerald-600 ml-2">
                    Target: ${emergencyFundData.calculations?.target_amount?.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/emergency-fund"
                onClick={(e) => e.stopPropagation()}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
              >
                Go to Fund →
              </Link>
              <svg className="w-4 h-4 text-emerald-600 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </summary>
          <div className="px-3 pb-3 pt-1 text-sm text-emerald-700">
            Your bills determine your emergency fund target. Toggle the shield icon on bills to include/exclude them from the calculation.
          </div>
        </details>
      )}

      {/* Add/Edit Form - More Compact */}
      {showAddForm && (
        <div ref={formRef} className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {editingBill ? 'Edit Bill' : 'Add New Bill'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bill Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="bill-name-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Electricity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount ($) *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  data-testid="bill-amount-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  data-testid="bill-category-select"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {getExistingCategories().map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                  <option value="CUSTOM">➕ Add New Category</option>
                </select>
                {formData.category === 'CUSTOM' && (
                  <input
                    type="text"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="Enter category name (e.g., Transportation)"
                    data-testid="custom-category-input"
                    className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Day *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  max="31"
                  value={formData.due_day}
                  onChange={(e) => setFormData({ ...formData, due_day: parseInt(e.target.value) })}
                  data-testid="bill-due-day-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frequency *
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  data-testid="bill-frequency-select"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Every 2 Weeks">Every 2 Weeks</option>
                  <option value="Annual">Annual (Once per year)</option>
                </select>
                {formData.frequency === 'Annual' && (
                  <p className="text-xs text-blue-600 mt-1">
                    💡 Annual bills only appear in the month you select below
                  </p>
                )}
              </div>
              {formData.frequency === 'Annual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Which Month is This Bill Due? *
                  </label>
                  <select
                    value={formData.annual_month}
                    onChange={(e) => setFormData({ ...formData, annual_month: parseInt(e.target.value) })}
                    data-testid="bill-annual-month-select"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {MONTHS.map((month, index) => (
                      <option key={month} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Example: Car insurance renewal in March, property tax in April
                  </p>
                </div>
              )}
            </div>
            
            {/* Email Reminders Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="mb-3">
                <span className="text-sm font-semibold text-gray-900">
                  📧 Email Reminder Options
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  Choose when you want to be notified before this bill is due
                </p>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.reminder_days?.includes(7)}
                    onChange={() => handleReminderToggle(7)}
                    data-testid="reminder-7-days"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">7 days before</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.reminder_days?.includes(3)}
                    onChange={() => handleReminderToggle(3)}
                    data-testid="reminder-3-days"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">3 days before</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.reminder_days?.includes(1)}
                    onChange={() => handleReminderToggle(1)}
                    data-testid="reminder-1-day"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">1 day before</span>
                </label>
              </div>
            </div>

            {/* Subscription Checkbox */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_subscription}
                  onChange={(e) => setFormData({ ...formData, is_subscription: e.target.checked })}
                  data-testid="is-subscription-checkbox"
                  className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-semibold text-gray-900">🔄 This is a subscription</span>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Mark recurring services like Netflix, Spotify, gym memberships, etc.
                  </p>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                data-testid="save-bill-button"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                {editingBill ? 'Update Bill' : 'Add Bill'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                data-testid="cancel-bill-button"
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bills List */}
      {bills.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Bills Yet</h3>
          <p className="text-gray-600">Add your first bill to get started with budgeting</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Subscriptions Section - Always at top when there are subscriptions */}
          {subscriptionBills.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔄</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Subscriptions ({subscriptionBills.length})
                    </h3>
                    <p className="text-sm text-purple-600">
                      These feed into your Tips & Insights
                    </p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  ${subscriptionBills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}/mo
                </span>
              </div>
              <div className="space-y-2">
                {subscriptionBills.map((bill) => (
                  <div 
                    key={bill.id} 
                    ref={(el) => billRefs.current[bill.id] = el}
                  >
                  <SwipeableRow
                    onSwipeLeft={() => handleDelete(bill.id)}
                    onSwipeRight={() => handleEdit(bill)}
                    leftLabel="Delete"
                    rightLabel="Edit"
                    rightColor="#3B82F6"
                  >
                    <div
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors gap-3"
                      data-testid={`subscription-item-${bill.id}`}
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                          {bill.name}
                          {bill.is_ecosystem_charge && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              bill.platform === 'apple' 
                                ? 'bg-gray-900 text-white' 
                                : 'bg-blue-500 text-white'
                            }`}>
                              {bill.platform === 'apple' ? '🍎' : '🔷'} via {bill.platform === 'apple' ? 'Apple' : 'Google'}
                            </span>
                          )}
                          {bill.reminder_days && bill.reminder_days.length > 0 && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              📧 Reminders
                            </span>
                          )}
                        </h4>
                        {bill.original_plaid_name && bill.original_plaid_name !== bill.name && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            <span className="text-gray-400">Original:</span> {bill.original_plaid_name}
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          Due Day {bill.due_day} | {bill.frequency}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Move to Bills button */}
                        <button
                          onClick={() => toggleSubscription(bill.id, true)}
                          title="Move to regular Bills"
                          className="p-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {/* Emergency Fund Toggle */}
                        <button
                          onClick={() => toggleEmergencyFund(bill.id, bill.include_in_emergency_fund === false)}
                          title={bill.include_in_emergency_fund === false ? "Add to Emergency Fund" : "Remove from Emergency Fund"}
                          className={`p-2 rounded-lg transition-all ${
                            bill.include_in_emergency_fund === false
                              ? 'bg-gray-100 text-gray-400 hover:bg-emerald-100 hover:text-emerald-600'
                              : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                          }`}
                        >
                          🛡️
                        </button>
                        <span className="text-xl font-bold text-purple-700">
                          ${bill.amount.toFixed(2)}
                        </span>
                        <div className="hidden sm:flex gap-2">
                          <button
                            onClick={() => handleEdit(bill)}
                            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(bill.id)}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </SwipeableRow>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regular Bills by Category */}
          {Object.entries(groupedRegularBills).map(([category, categoryBills]) => (
            <div key={category} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {category || '📋 Other Bills'} ({categoryBills.length})
              </h3>
              <div className="space-y-2">
                {categoryBills.map((bill) => (
                  <div 
                    key={bill.id} 
                    ref={(el) => billRefs.current[bill.id] = el}
                  >
                  <SwipeableRow
                    onSwipeLeft={() => handleDelete(bill.id)}
                    onSwipeRight={() => handleEdit(bill)}
                    leftLabel="Delete"
                    rightLabel="Edit"
                    rightColor="#3B82F6"
                  >
                    <div
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                      data-testid={`bill-item-${bill.id}`}
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                          {bill.name}
                          {bill.is_subscription && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                              🔄 Subscription
                            </span>
                          )}
                          {/* Ecosystem Charge Badge with Rename Option */}
                          {bill.is_ecosystem_charge && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              bill.platform === 'apple' 
                                ? 'bg-gray-900 text-white' 
                                : 'bg-blue-500 text-white'
                            }`}>
                              {bill.platform === 'apple' ? '🍎' : '🔷'} via {bill.platform === 'apple' ? 'Apple' : 'Google'}
                            </span>
                          )}
                          {bill.reminder_days && bill.reminder_days.length > 0 && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              📧 Reminders: {bill.reminder_days.sort((a, b) => b - a).join(', ')} days
                            </span>
                          )}
                        </h4>
                        {/* Show original Plaid name if different from display name */}
                        {bill.original_plaid_name && bill.original_plaid_name !== bill.name && (
                          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                            <span className="text-gray-400">Original:</span> 
                            <span className="font-mono bg-gray-100 px-1 rounded">{bill.original_plaid_name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRenameEcosystemBill(bill);
                              }}
                              className="ml-2 text-amber-600 hover:text-amber-800 font-medium"
                              data-testid={`rename-bill-${bill.id}`}
                            >
                              ✏️ Rename
                            </button>
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          Due Day {bill.due_day} | {bill.frequency}
                          {bill.frequency === 'Annual' && ` (${MONTHS[bill.annual_month - 1]})`}
                        </p>
                        {/* Mobile swipe hint - more explicit */}
                        <div className="flex items-center gap-2 mt-1 sm:hidden text-xs">
                          <span className="flex items-center text-blue-500">
                            <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Swipe right to Edit
                          </span>
                          <span className="text-gray-300">|</span>
                          <span className="flex items-center text-red-500">
                            Swipe left to Delete
                            <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Move to Subscriptions button */}
                        <button
                          onClick={() => toggleSubscription(bill.id, false)}
                          title="Mark as Subscription"
                          className="p-2 rounded-lg bg-purple-50 text-purple-400 hover:bg-purple-100 hover:text-purple-600 transition-colors"
                        >
                          🔄
                        </button>
                        {/* Emergency Fund Toggle */}
                        <button
                          onClick={() => toggleEmergencyFund(bill.id, bill.include_in_emergency_fund === false)}
                          title={bill.include_in_emergency_fund === false ? "Add to Emergency Fund" : "Remove from Emergency Fund"}
                          className={`p-2 rounded-lg transition-all ${
                            bill.include_in_emergency_fund === false
                              ? 'bg-gray-100 text-gray-400 hover:bg-emerald-100 hover:text-emerald-600'
                              : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                          }`}
                        >
                          🛡️
                        </button>
                        <span className="text-xl font-bold text-gray-900">
                          ${bill.amount.toFixed(2)}
                        </span>
                        <div className="hidden sm:flex gap-2">
                          <button
                            onClick={() => handleEdit(bill)}
                            data-testid={`edit-bill-${bill.id}`}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(bill.id)}
                            data-testid={`delete-bill-${bill.id}`}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </SwipeableRow>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="font-semibold text-gray-700">Category Total</span>
                <span className="text-xl font-bold text-gray-900">
                  ${categoryBills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}
                </span>
              </div>
            </div>
          ))}

          {/* Grand Total */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-medium">Bills</span>
              <span className="text-xl font-bold">
                ${regularBills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}
              </span>
            </div>
            {subscriptionBills.length > 0 && (
              <div className="flex justify-between items-center mb-2 opacity-90">
                <span className="text-lg font-medium">Subscriptions</span>
                <span className="text-xl font-bold">
                  ${subscriptionBills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}
                </span>
              </div>
            )}
            <div className="border-t border-white/30 pt-3 mt-3 flex justify-between items-center">
              <span className="text-xl font-semibold">Total Monthly</span>
              <span className="text-3xl font-bold">
                ${bills.reduce((sum, bill) => sum + bill.amount, 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Debt Payments Section */}
      {debtBills.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6 border border-purple-200">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">💳</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Debt Payments ({debtBills.length})
              </h3>
              <p className="text-sm text-gray-500">
                These payments are pulled from your Debt Snowball Calculator
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            {debtBills.map((debt) => (
              <div
                key={debt.id}
                className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-purple-50 rounded-lg gap-3"
                data-testid={`debt-bill-item-${debt.id}`}
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">
                    {debt.name}
                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                      From Debt Snowball
                    </span>
                  </h4>
                  <p className="text-sm text-gray-600">
                    Due Day {debt.due_day} | Balance: ${debt.debt_balance?.toLocaleString()} @ {debt.debt_apr}% APR
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xl font-bold text-purple-700">
                    ${debt.amount.toFixed(2)}
                  </span>
                  <a
                    href="/debt-snowball"
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition-colors text-sm"
                  >
                    Edit in Calculator
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-purple-200 flex justify-between items-center">
            <span className="font-semibold text-gray-700">Total Debt Payments</span>
            <span className="text-xl font-bold text-purple-700">
              ${debtBills.reduce((sum, debt) => sum + debt.amount, 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Combined Total with Debts */}
      {debtBills.length > 0 && bills.length > 0 && (
        <div className="mt-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center">
            <span className="text-xl font-semibold">Combined Total (Bills + Debt Payments)</span>
            <span className="text-3xl font-bold">
              ${(bills.reduce((sum, bill) => sum + bill.amount, 0) + debtBills.reduce((sum, debt) => sum + debt.amount, 0)).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Rename Ecosystem Bill Modal */}
      {showRenameModal && renamingBill && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                  🤖
                </div>
                <div>
                  <h3 className="font-bold text-lg">Rename This Bill</h3>
                  <p className="text-amber-100 text-sm">Give it a name you'll recognize</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Original name display */}
              <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Original from {renamingBill.platform === 'apple' ? 'Apple' : 'Google'}:</p>
                <p className="font-mono text-sm text-gray-700">{renamingBill.original_plaid_name}</p>
                <p className="text-sm font-bold text-gray-900 mt-2">${renamingBill.amount?.toFixed(2)}/month</p>
              </div>

              {/* Name input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What is this subscription?
                </label>
                <input
                  type="text"
                  value={newBillName}
                  onChange={(e) => setNewBillName(e.target.value)}
                  placeholder="e.g., Netflix, Spotify, iCloud..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
                  autoFocus
                  data-testid="rename-input"
                />
              </div>

              {/* Quick suggestions */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Quick picks:</p>
                <div className="flex flex-wrap gap-2">
                  {(renamingBill.platform === 'apple' 
                    ? ['Netflix', 'Spotify', 'iCloud', 'Apple Music', 'Apple TV+', 'Disney+', 'HBO Max']
                    : ['YouTube Premium', 'Google One', 'Spotify', 'Netflix', 'HBO Max', 'Hulu', 'Disney+']
                  ).map(suggestion => (
                    <button
                      key={suggestion}
                      onClick={() => setNewBillName(suggestion)}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-amber-100 text-gray-700 hover:text-amber-700 rounded-full transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-gray-50 flex gap-3">
              <button
                onClick={() => {
                  setShowRenameModal(false);
                  setRenamingBill(null);
                  setNewBillName('');
                }}
                className="flex-1 py-3 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitRename}
                disabled={!newBillName.trim()}
                className="flex-1 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="rename-submit-btn"
              >
                Save Name
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alias Catcher Modal for Plaid Ecosystem Bills */}
      {showAliasCatcher && ecosystemBills.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl my-8">
            {/* Guapo Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 rounded-t-2xl">
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-3xl">
                  🤖
                </div>
                <div className="text-white">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg">GUAPO</span>
                    <span className="px-2 py-0.5 bg-white/20 text-xs rounded-full">Alias Catcher</span>
                  </div>
                  <p className="text-sm text-amber-100 mt-1">
                    <span className="font-bold">Hold up!</span> I found {ecosystemBills.length} charges from Apple/Google. 
                    They hide the real subscription names. What are these for?
                  </p>
                </div>
              </div>
            </div>

            {/* Bills to Rename */}
            <div className="p-4 max-h-96 overflow-y-auto space-y-3">
              {ecosystemBills.map((bill, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      bill.platform === 'apple' ? 'bg-gray-900 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {bill.platform === 'apple' ? '🍎 Apple' : '🔷 Google'}
                    </span>
                    <span className="font-bold text-gray-900">${bill.amount?.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-500 font-mono mb-3">"{bill.original_plaid_name}"</p>
                  <input
                    type="text"
                    placeholder="What is this? (Netflix, Spotify...)"
                    value={aliases[idx] || ''}
                    onChange={(e) => setAliases(prev => ({ ...prev, [idx]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-200 outline-none"
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(bill.platform === 'apple' 
                      ? ['Netflix', 'Spotify', 'iCloud', 'Apple Music', 'Disney+']
                      : ['YouTube Premium', 'Google One', 'Spotify', 'Netflix', 'HBO Max']
                    ).map(s => (
                      <button
                        key={s}
                        onClick={() => setAliases(prev => ({ ...prev, [idx]: s }))}
                        className="px-2 py-0.5 text-xs bg-gray-200 hover:bg-amber-100 rounded-full"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="p-4 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => {
                  setShowAliasCatcher(false);
                  setPlaidBills(prev => [...prev, ...ecosystemBills]);
                  setShowPlaidImport(true);
                }}
                className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
              >
                Skip
              </button>
              <button
                onClick={handleAliasComplete}
                className="flex-1 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600"
              >
                Save Names
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plaid Import Review Modal */}
      {showPlaidImport && plaidBills.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl my-8">
            <div className="p-6 text-center border-b">
              <div className="text-4xl mb-2">🎉</div>
              <h2 className="text-xl font-bold text-gray-900">Found {plaidBills.length} Bills!</h2>
              <p className="text-gray-500 text-sm">Select which bills to import</p>
            </div>

            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {plaidBills.map((bill, idx) => (
                <label
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    defaultChecked
                    className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    data-bill-index={idx}
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">{bill.name}</span>
                    {bill.is_ecosystem_charge && (
                      <span className={`ml-2 px-1.5 py-0.5 text-[10px] rounded ${
                        bill.platform === 'apple' ? 'bg-gray-900 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {bill.platform === 'apple' ? '🍎' : '🔷'}
                      </span>
                    )}
                    <p className="text-xs text-gray-500">{bill.category || 'Subscription'} • {bill.frequency || 'Monthly'}</p>
                  </div>
                  <span className="font-bold text-gray-900">${bill.amount?.toFixed(2)}</span>
                </label>
              ))}
            </div>

            <div className="p-4 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => {
                  setShowPlaidImport(false);
                  setPlaidBills([]);
                }}
                className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const checkboxes = document.querySelectorAll('[data-bill-index]');
                  const selected = plaidBills.filter((_, idx) => checkboxes[idx]?.checked);
                  handleImportPlaidBills(selected);
                }}
                disabled={plaidLoading}
                className="flex-1 py-3 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 disabled:opacity-50"
              >
                {plaidLoading ? 'Importing...' : `Import ${plaidBills.length} Bills`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Bills;